import React, { useEffect, useState } from "react";
import { CartItemModal } from "../caterer-details/organisms/cart-item-modal/cart-item-modal";
import { Link } from "react-router-dom";
import SideMenu from "../../components/organisms/sidebar";
import LoggedHeader from "../../Modules/Common/loggedHeader";
import { Button, TextField, Box, Typography, Alert, Autocomplete } from "@mui/material";
import {
  useEditOrderState,
  useEditOrderMutations,
  useCatererMenuOptions,
} from "./edit-order-manual.state";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { DatePickerForm } from "@components/datepicker-form";
import { ICartItem } from "@services/redux/order/order.typings";
import {
  buildCartSubmission,
  describeUnidentifiedRows,
  findUnidentifiedRows,
  getCartItemMode,
  newCartRow,
} from "./edit-order-manual.utils";

// Extend ICartItem with UI-only field for react-hook-form
type CartItem = ICartItem & { markedForDelete?: boolean };

type EditOrderForm = {
  deliveryDateTime: Date | null;
  contactPerson: string;
  contactNo: string;
  contactEmail: string;
  deliveryMode: string;
  locationName: string;
  instruction: string;
  street: string;
  tablewareTotal: number;
  deliveryFee: number;
  convenienceFee: number;
  salesTaxPercentage: number;
  salesTaxAmount: number;
  driverTip: number;
  headCount: number;
  cartDetails: CartItem[];
};

const formatCurrency = (value: number | string) => {
  const num = Number(
    typeof value === "string" ? value.replace(/,/g, "") : value
  );
  return isNaN(num) ? "" : num.toLocaleString(undefined, { minimumFractionDigits: 2 });
};

const EditOrderManual = () => {
  const { order, orderLoading, updateLoading, handleUpdateOrder, orderId, refetchOrder, renderItemSummary } = useEditOrderState();

  const { fetchMenu } = useEditOrderMutations();
  // MAIN-881: why the last save did not happen, shown above the Save button.
  const [saveError, setSaveError] = useState<string | null>(null);
  // MAIN-812: menu items for this order's caterer, so items are picked by name.
  const { menuOptions, menuOptionsLoading } = useCatererMenuOptions(order?.catererId);

  // Modal state for CartItemModal
  const [isCartModalOpen, setIsCartModalOpen] = useState(false);
  const [selectedCartIndex, setSelectedCartIndex] = useState<number | null>(null);


  const { control, handleSubmit, setValue, getValues, watch, reset } = useForm<EditOrderForm>();
  // Alias for setValue to allow dynamic paths for nested fields
  const setNestedValue = setValue as unknown as (name: string, value: any) => void;
  const { fields: cartFields, append, remove } = useFieldArray({
    control,
    name: "cartDetails"
  });

  useEffect(() => {
    if (order) {
      reset({
        deliveryDateTime: order.deliveryDate ? new Date(order.deliveryDate) : null,
        contactPerson: order.contactPerson || "",
        contactNo: order.contactNo || "",
        contactEmail: order.customerDetails?.email || "",
        deliveryMode: order.deliveryMode || "",
        locationName: order.locationName || "",
        instruction: order.instruction || "",
        street: order.street || "",
        tablewareTotal: order.tablewareTotal || 0,
        deliveryFee: order.deliveryFee || 0,
        convenienceFee: order.convenienceFee || 0,
        salesTaxPercentage: order.salesTaxPercentage || 0,
        driverTip: order.driverTip || 0,
        headCount: order.headCount || 0,
        cartDetails: order?.cartDetails || []
      });
    }
  }, [order, reset]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onSubmit = async (formValues) => {
    const {
      convenienceFee,
      deliveryDateTime,
      cartDetails,
      street,
      contactEmail,
      deliveryMode,
      locationName,
      ...rest
    } = formValues;

    // MAIN-881: a row without a menu item used to be saved as a $0 line with
    // no name. Stop the save and say which rows need attention instead.
    const unidentified = describeUnidentifiedRows(findUnidentifiedRows(cartDetails));
    if (unidentified) {
      setSaveError(unidentified);
      return;
    }
    setSaveError(null);

    // Rows marked for removal are left out; the API deletes any saved row it
    // no longer receives, inside the same payment-safe edit.
    const sanitizedCart = buildCartSubmission(
      cartDetails,
      order?.orderId || order?._id || formValues?.orderId,
    );

    const result = await handleUpdateOrder(
      {
        ...rest,
        street,
        deliveryDate: deliveryDateTime,
        deliveryTime: order?.deliveryTime || "",
        commision: Number(convenienceFee),
        catererId: order?.catererId,
        orderId: order?.orderId || order?._id,
        headCount: Number(rest.headCount),
        salesTaxPercentage: Number(rest.salesTaxPercentage),
        deliveryMode,
        customerId: order?.customerId,
        locationName
        // customerDetails removed from payload
        // contactEmail excluded from mutation payload
        // commisionAmount intentionally omitted
      },
      sanitizedCart
    );

    // MAIN-881: removals used to be sent again as separate deleteCart calls
    // AFTER the save, even when the save had failed — so a refused payment
    // re-authorization, which rolls the order back, still lost the removed
    // rows. The save itself already deletes them, so nothing else is sent.
    if (!result?.success) {
      setSaveError(
        (result?.error as any)?.message ||
          "The order could not be saved. Nothing was changed."
      );
      return;
    }
    await refetchOrder();
  };


  // CartItemEditor component to scope useMenuAddOns per cart item
  const CartItemEditor = ({ field, idx }) => {
    // Use parent's form context
    // eslint-disable-next-line
    // MAIN-881: the row's own values decide what it shows. A page-wide menu
    // lookup used to, which turned every row added after the first into a $0
    // "saved item" with no picker.
    const row = watch(`cartDetails.${idx}` as const) || field;
    const mode = getCartItemMode(row);
    if (mode === "removed") {
      return (
        <div
          key={field.id}
          className="item-marked-for-delete"
          style={{
            opacity: 0.5,
            border: "1px dashed #999",
            padding: "1rem",
            marginBottom: "1rem",
            borderRadius: "4px",
            backgroundColor: "#f3f3f3",
            marginTop: "1.5rem" // match spacing for consistency
          }}
        >
          <p><strong>Item #{idx + 1}</strong> — <em>Pending Removal</em></p>
        </div>
      );
    }
    // A saved row with no menu item behind it (created by the old bug) can
    // only be removed; it is never shown as a $0 item.
    if (mode === "unidentified") {
      return (
        <div key={field.id} className="order-item-box" style={{ marginTop: "1.5rem" }}>
          <p><strong>Item #{idx + 1}</strong></p>
          <Alert severity="warning" sx={{ mt: 1 }}>
            This saved row is not linked to any menu item. Remove it, then add
            the item again from the menu.
          </Alert>
          <Button
            variant="contained"
            color="error"
            sx={{ mt: 1 }}
            onClick={() => setNestedValue(`cartDetails.${idx}.markedForDelete`, true)}
          >
            Remove Item
          </Button>
          <Box sx={{ borderTop: "1px solid #e0e0e0", mt: 3, mb: 2 }} />
        </div>
      );
    }
    // A new row that still needs a menu item: the picker.
    if (mode === "picker") {
      return (
        <div key={field.id} className="order-item-box" style={{ marginTop: "1.5rem" }}>
          <p><strong>Item #{idx + 1}</strong></p>
          {/* MAIN-812: pick the item by name from this caterer's menu instead
              of typing a raw menu id. Selecting an item opens the same
              quantity/add-on dialog the previous "Find" button opened. */}
          <Controller
            name={`cartDetails.${idx}.menuId`}
            control={control}
            defaultValue={field.menuId}
            render={({ field: menuField }) => (
              <Autocomplete
                options={menuOptions}
                loading={menuOptionsLoading}
                getOptionLabel={(option: any) =>
                  option?.itemName
                    ? `${option.itemName}${
                        option.price != null ? ` — $${Number(option.price).toFixed(2)}` : ""
                      }`
                    : ""
                }
                isOptionEqualToValue={(option: any, value: any) =>
                  option?._id === value?._id
                }
                noOptionsText={
                  menuOptionsLoading ? "Loading menu…" : "No menu items found"
                }
                onChange={async (_event, selected: any) => {
                  if (!selected?._id) return;
                  menuField.onChange(selected._id);
                  try {
                    const result = await fetchMenu({
                      variables: { getOneMenuId: selected._id },
                    });
                    if (result?.data?.getOneMenu) {
                      setSelectedCartIndex(idx);
                      setIsCartModalOpen(true);
                    }
                  } catch (err) {
                    // Silently ignore or handle as needed
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search this caterer's menu"
                    helperText="Start typing an item name, then choose it to set quantity and add-ons."
                    fullWidth
                    variant="outlined"
                    className="allFeild"
                  />
                )}
              />
            )}
          />
          <Button
            variant="text"
            color="error"
            sx={{ mt: 1 }}
            onClick={() => remove(idx)}
          >
            Remove row
          </Button>
          {/* Divider below Find for new (incomplete) items */}
          <Box sx={{ borderTop: "1px solid #e0e0e0", mt: 3, mb: 2 }} />
        </div>
      );
    }
    // Otherwise, show item summary/edit (no menuId field for existing items)
    return (
      <div key={field.id} className="order-item-box" style={{ marginTop: "1.5rem" }}>
        <p><strong>Item #{idx + 1}</strong></p>
        {/* Item Summary section */}
        <Box mt={2}>
          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
            Item Summary:
          </Typography>
          <Box sx={{ pl: 1, pt: 0.5 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
              {renderItemSummary(row)}
            </Typography>
          </Box>
        </Box>
        {/* Not editable message and Remove Item button, left-aligned and spaced */}
        <Box sx={{ display: "flex", gap: "1rem", mt: 1, alignItems: "center" }}>
          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: '#666' }}>
            To change this item, remove it and add it again with the new quantity or add-ons.
          </Typography>
          <Button
            variant="contained"
            color="error"
            onClick={() => setNestedValue(`cartDetails.${idx}.markedForDelete`, true)}
          >
            Remove Item
          </Button>
        </Box>
        {/* Separator after each item */}
        <Box sx={{ borderTop: "1px solid #e0e0e0", mt: 3, mb: 2 }} />
      </div>
    );
  };

  return (
    <>
      <div>
        <LoggedHeader selected={"Orders"} />
        <SideMenu selected="Orders" user="caterer" />
        <div className="contentWrap contentWrap-order-view">
          <div>
            <div className="row">
              <div className="col-lg-7 col-md-12">
                <Link to={"/orders/" + orderId} className="back-link-order">
                  <img src="../assets/images/back-org.svg" className="img-fluid" alt="Back" />
                  <span>Back to order</span>
                </Link>
                <div className="common-drawer">
                  <div className="drawer-head">
                    <h4 className="h4">
                      Edit Order (Admin)
                    </h4>
                  </div>
                  {/* MAIN-812: state plainly who can edit and what saving does
                      to the customer's payment, so admins are not guessing. */}
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>Admin edit.</strong> You can edit this order at any
                    time, including same day. Customers can only edit their own
                    order while delivery is more than 24 hours away. The order
                    keeps its original order number and customer.
                    <br />
                    <strong>Payment:</strong> if the new total is higher, the
                    difference is charged to the customer&apos;s saved card when
                    the order is completed — the original charge is not
                    duplicated. If the new total is lower, nothing extra is
                    charged and only the reduced amount is collected. Saving may
                    return the order to Pending for the restaurant to re-confirm.
                  </Alert>
                  <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
                    <div className="create-step-form">
                      <div className="row">
                        <div className="col-md-12 textBox" style={{ maxWidth: "400px", marginBottom: "1.5rem" }}>
                          <DatePickerForm
                            control={control}
                            name="deliveryDateTime"
                            rules={{
                              required: "Date & Time are required",
                            }}
                            label="Delivery Date & Time"
                            onUpdate={(name, value) => setValue(name, value)}
                          />
                        </div>
                        <div className="col-md-12 textBox">
                          <Controller
                            name="contactPerson"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Contact Person"
                                fullWidth
                                variant="outlined"
                                className="allFeild"
                              />
                            )}
                          />
                        </div>
                        <div className="col-md-12 textBox">
                          <Controller
                            name="contactNo"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Contact Number"
                                fullWidth
                                variant="outlined"
                                className="allFeild"
                              />
                            )}
                          />
                        </div>
                        <div className="col-md-12 textBox">
                          <Controller
                            name="contactEmail"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Contact Email"
                                fullWidth
                                variant="outlined"
                                className="allFeild"
                              />
                            )}
                          />
                        </div>
                        <div className="col-md-12 textBox">
                          <Controller
                            name="deliveryMode"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Delivery Mode"
                                fullWidth
                                variant="outlined"
                                className="allFeild"
                              />
                            )}
                          />
                        </div>
                        <div className="col-md-12 textBox">
                          <Controller
                            name="locationName"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Delivery Location Name"
                                fullWidth
                                variant="outlined"
                                className="allFeild"
                              />
                            )}
                          />
                        </div>
                        <div className="col-md-12 textBox">
                          <Controller
                            name="instruction"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Delivery Instructions"
                                fullWidth
                                multiline
                                rows={4}
                                variant="outlined"
                                className="allFeild"
                              />
                            )}
                          />
                        </div>
                        <div className="col-md-12 textBox">
                          <Controller
                            name="street"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Street Address"
                                fullWidth
                                multiline
                                rows={2}
                                variant="outlined"
                                className="allFeild"
                              />
                            )}
                          />
                        </div>
                        <div className="col-md-12 textBox">
                          <Controller
                            name="tablewareTotal"
                            control={control}
                            defaultValue={0}
                            render={({ field: { value, onChange, onBlur, ref } }) => (
                              <TextField
                                label="Tableware Total"
                                inputRef={ref}
                                type="text"
                                inputProps={{ inputMode: "decimal", pattern: "[0-9,.]*", step: "0.01" }}
                                value={value}
                                onChange={(e) => {
                                  const raw = e.target.value.replace(/[^0-9.,]/g, "");
                                  onChange(raw);
                                }}
                                onBlur={(e) => {
                                  const numeric = parseFloat(e.target.value.replace(/,/g, ""));
                                  onChange(isNaN(numeric) ? 0 : numeric);
                                }}
                                InputProps={{
                                  startAdornment: <span style={{ marginRight: 4 }}>$</span>
                                }}
                                fullWidth
                                variant="outlined"
                                className="allFeild"
                              />
                            )}
                          />
                        </div>
                        <div className="col-md-12 textBox">
                          <Controller
                            name="deliveryFee"
                            control={control}
                            defaultValue={0}
                            render={({ field: { value, onChange, onBlur, ref } }) => (
                              <TextField
                                label="Delivery Fee"
                                inputRef={ref}
                                type="text"
                                inputProps={{ inputMode: "decimal", pattern: "[0-9,.]*", step: "0.01" }}
                                value={value}
                                onChange={(e) => {
                                  const raw = e.target.value.replace(/[^0-9.,]/g, "");
                                  onChange(raw);
                                }}
                                onBlur={(e) => {
                                  const numeric = parseFloat(e.target.value.replace(/,/g, ""));
                                  onChange(isNaN(numeric) ? 0 : numeric);
                                }}
                                InputProps={{
                                  startAdornment: <span style={{ marginRight: 4 }}>$</span>
                                }}
                                fullWidth
                                variant="outlined"
                                className="allFeild"
                              />
                            )}
                          />
                        </div>
                        <div className="col-md-12 textBox">
                          <Controller
                            name="convenienceFee"
                            control={control}
                            defaultValue={0}
                            render={({ field: { value, onChange, ref } }) => (
                              <TextField
                                label="Service Fee"
                                inputRef={ref}
                                type="text"
                                inputProps={{ inputMode: "decimal", pattern: "[0-9,.]*", step: "0.01" }}
                                value={value}
                                onChange={(e) => {
                                  const raw = e.target.value.replace(/[^0-9.,]/g, "");
                                  onChange(raw);
                                }}
                                onBlur={(e) => {
                                  const numeric = parseFloat(e.target.value.replace(/,/g, ""));
                                  onChange(isNaN(numeric) ? 0 : numeric);
                                }}
                                InputProps={{
                                  startAdornment: <span style={{ marginRight: 4 }}>$</span>
                                }}
                                fullWidth
                                variant="outlined"
                                className="allFeild"
                              />
                            )}
                          />
                        </div>
                        <div className="col-md-12 textBox">
                          <Controller
                            name="salesTaxPercentage"
                            control={control}
                            defaultValue={0}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Tax Percentage"
                                type="number"
                                fullWidth
                                InputProps={{ endAdornment: <span style={{ marginLeft: 4 }}>%</span> }}
                                variant="outlined"
                                className="allFeild"
                              />
                            )}
                          />
                        </div>
                        <div className="col-md-12 textBox">
                          <Controller
                            name="driverTip"
                            control={control}
                            defaultValue={0}
                            render={({ field: { value, onChange, onBlur, ref } }) => (
                              <TextField
                                label="Tip Total"
                                inputRef={ref}
                                type="text"
                                inputProps={{ inputMode: "decimal", pattern: "[0-9,.]*", step: "0.01" }}
                                value={value}
                                onChange={(e) => {
                                  const raw = e.target.value.replace(/[^0-9.,]/g, "");
                                  onChange(raw);
                                }}
                                onBlur={(e) => {
                                  const numeric = parseFloat(e.target.value.replace(/,/g, ""));
                                  onChange(isNaN(numeric) ? 0 : numeric);
                                }}
                                InputProps={{
                                  startAdornment: <span style={{ marginRight: 4 }}>$</span>
                                }}
                                fullWidth
                                variant="outlined"
                                className="allFeild"
                              />
                            )}
                          />
                        </div>
                        <div className="col-md-12 textBox">
                          <Controller
                            name="headCount"
                            control={control}
                            defaultValue={0}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Headcount"
                                type="number"
                                fullWidth
                                variant="outlined"
                                className="allFeild"
                              />
                            )}
                          />
                        </div>
                        <div className="col-md-12">
                          <h5 style={{ marginTop: "2rem" }}>Order Items</h5>
                          {cartFields.map((field, idx) => (
                            <CartItemEditor key={field.id} field={field} idx={idx} />
                          ))}
                          <Button
                            variant="outlined"
                            color="primary"
                            style={{ marginBottom: "2rem" }}
                            onClick={() =>
                              append(newCartRow() as CartItem)
                            }
                          >
                            Add New Item
                          </Button>
                        </div>
                      </div>
                    </div>
                    {saveError && (
                      <Alert severity="error" role="alert" sx={{ mt: 2 }}>
                        {saveError}
                      </Alert>
                    )}
                    <div className="createStepBtn common-drawer" style={{ display: "flex", justifyContent: "flex-end", marginTop: "2rem" }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        className="saveBtn"
                        disabled={updateLoading}
                      >
                        {updateLoading ? "Saving..." : "Save Changes"}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
              <div className="col-lg-5 col-md-12">
                <div className="order-view order-financial-box">
                  <div className="order-detail-box">
                    <div className="order-title-box">
                      <h4 className="h4">Original Order</h4>
                    </div>
                    <div className="order-summary">
                      <p><strong>Delivery Date:</strong> {order?.deliveryDate?.slice(0, 10)}</p>
                      <p><strong>Delivery Time:</strong> {order?.deliveryTime}</p>
                      <p><strong>Instructions:</strong> {order?.instruction}</p>
                      <p><strong>Delivery Address:</strong> {order?.street}</p>
                      <p><strong>Customer ID:</strong> {order?.customerId}</p>
                      {/* Cart Items summary */}
                      {order?.cartDetails?.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                            Items:
                          </Typography>
                          {order.cartDetails.map((item, idx) => (
                            <Box key={item._id || idx} sx={{ mb: 1.5, pl: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item.itemName || "Item"} {typeof item.quantity === "number" ? `x${item.quantity}` : ""} — ${item.pricePerUnit?.toFixed(2)} each (${item.cartTotalPrice?.toFixed(2)})
                              </Typography>
                              {/* Add-ons */}
                              {item.addOns?.length > 0 && (
                                <Box sx={{ pl: 2, mt: 0.25 }}>
                                  {item.addOns.map((addOn, addOnIdx) => (
                                    <Box key={addOn._id || addOnIdx} sx={{ mb: 0.5 }}>
                                      <Typography variant="body2">
                                        - {addOn.addOnName}
                                        {addOn.addOnTypeName ? ` (${addOn.addOnTypeName})` : ""}
                                        {typeof addOn.addOnQuantity === "number" && ` x${addOn.addOnQuantity}`}
                                        {typeof addOn.totalPrice === "number" && (
                                          <> — ${Number(addOn.totalPrice).toLocaleString(undefined, { minimumFractionDigits: 2 })}</>
                                        )}
                                      </Typography>
                                      {/* Sub-add-ons */}
                                      {addOn.subAddOns?.length > 0 && (
                                        <Box sx={{ pl: 2, mt: 0.25 }}>
                                          {addOn.subAddOns.map((sub, subIdx) => (
                                            <Typography variant="body2" sx={{ color: "#888" }} key={sub._id || subIdx}>
                                              • {sub.name}
                                              {typeof sub.quantity === "number" && ` x${sub.quantity}`}
                                              {typeof sub.price === "number" && (
                                                <> — ${Number(sub.price).toLocaleString(undefined, { minimumFractionDigits: 2 })}</>
                                              )}
                                            </Typography>
                                          ))}
                                        </Box>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </div>
                    <div className="order-financial-row">
                      <div className="review-order-price d-flex justify-content-between align-items-center py-1">
                        <span className="label text-start" style={{ flex: 1 }}>Subtotal:</span>
                        <span className="value text-end" style={{ flex: 1 }}>
                          {order?.totalPrice != null ? `$${order.totalPrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "0"}
                        </span>
                      </div>
                      <div className="review-order-price d-flex justify-content-between align-items-center py-1">
                        <span className="label text-start" style={{ flex: 1 }}>Tableware:</span>
                        <span className="value text-end" style={{ flex: 1 }}>
                          {order?.tablewareTotal != null ? `$${order.tablewareTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "0"}
                        </span>
                      </div>
                      <div className="review-order-price d-flex justify-content-between align-items-center py-1">
                        <span className="label text-start" style={{ flex: 1 }}>Delivery Fee:</span>
                        <span className="value text-end" style={{ flex: 1 }}>
                          {order?.deliveryFee != null ? `$${order.deliveryFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "0"}
                        </span>
                      </div>
                      <div className="review-order-price d-flex justify-content-between align-items-center py-1">
                        <span className="label text-start" style={{ flex: 1 }}>Service Fee:</span>
                        <span className="value text-end" style={{ flex: 1 }}>
                          {order?.convenienceFee != null ? `$${order.convenienceFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "0"}
                        </span>
                      </div>
                      <div className="review-order-price d-flex justify-content-between align-items-center py-1">
                        <span className="label text-start" style={{ flex: 1 }}>Tax:</span>
                        <span className="value text-end" style={{ flex: 1 }}>
                          {order?.salesTaxAmount != null ? `$${order.salesTaxAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "0"}
                          {typeof order?.salesTaxPercentage === "number" && (
                            <span style={{ marginLeft: 8, color: "#888", fontSize: "0.95em" }}>
                              ({order.salesTaxPercentage}%)
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="review-order-price d-flex justify-content-between align-items-center py-1">
                        <span className="label text-start" style={{ flex: 1 }}>Tip:</span>
                        <span className="value text-end" style={{ flex: 1 }}>
                          {order?.driverTip != null ? `$${order.driverTip.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "0"}
                        </span>
                      </div>
                      <div className="review-order-price d-flex justify-content-between align-items-center py-1">
                        <span className="label text-start" style={{ flex: 1 }}>Headcount:</span>
                        <span className="value text-end" style={{ flex: 1 }}>
                          {order?.headCount != null ? order.headCount.toLocaleString() : "—"}
                        </span>
                      </div>
                      {/* CaterCash Earned Row */}
                      <div className="review-order-price d-flex justify-content-between align-items-center py-1">
                        <span className="label text-start" style={{ flex: 1 }}><strong>CaterCash Earned</strong></span>
                        <span className="value text-end" style={{ flex: 1 }}>
                          {order?.caterCashTotal != null ? `$${order.caterCashTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—"}
                        </span>
                      </div>
                      <div className="review-order-price d-flex justify-content-between align-items-center py-1">
                        <span className="label text-start" style={{ flex: 1 }}>Grand Total:</span>
                        <span className="value text-end" style={{ flex: 1 }}>
                          {order?.grandTotal != null ? `$${order.grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "0"}
                        </span>
                      </div>
                      {/* Price Per Head Row */}
                      <div className="review-order-price d-flex justify-content-between align-items-center py-1">
                        <span className="label text-start" style={{ flex: 1 }}>
                          Price Per Head (Head Count: {order?.headCount ?? "—"}):
                        </span>
                        <span className="value text-end" style={{ flex: 1 }}>
                          {order?.totalPerHead != null ? `$${order.totalPerHead.toLocaleString(undefined, { minimumFractionDigits: 2 })}/person` : "—"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {selectedCartIndex !== null && (
        <CartItemModal
          isOpen={isCartModalOpen}
          menuId={getValues(`cartDetails.${selectedCartIndex}.menuId`)}
          cartItem={getValues(`cartDetails.${selectedCartIndex}`)}
          cartIndex={selectedCartIndex}
          cart={getValues("cartDetails")}
          customerId={order?.customerId || ""}
          onUpdateCart={(updatedCart) => {
            setValue("cartDetails", updatedCart);
            // Force update of form state to ensure display refresh
            setTimeout(() => {
              setSelectedCartIndex(null);
              setIsCartModalOpen(false);
            }, 0);
          }}
          onClose={() => setIsCartModalOpen(false)}
        />
      )}
    </>
  );
};

export default EditOrderManual;
